import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, createCustomer, bulkImportCustomers, getUsers, deleteCustomer, deleteAllCustomers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Upload, Search, Download, Trash2, Phone, Mail, MapPin, User, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useActionHistory } from '@/context/ActionHistoryContext';
import { safeToastError } from '@/utils/errorUtils';
import { getValidToken, handleTokenError } from '@/utils/tokenUtils';
import PageHeader from '@/components/PageHeader';

const Customers = ({ user }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRepresentative, setSelectedRepresentative] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const { pushAction } = useActionHistory();
  const itemsPerPage = 100;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    district: '',
    contact_person: '',
    representative: '',
    device_count: '0',
    tax_number: '',
    tax_office: ''
  });

  useEffect(() => {
    loadCustomers();
    loadUsers();
  }, []);

  // Benzersiz şehir listesi
  const uniqueCities = useMemo(() => {
    const cities = customers
      .map(c => c.city)
      .filter(city => city && city.trim() !== '');
    return [...new Set(cities)].sort((a, b) => a.localeCompare(b, 'tr'));
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    let filtered = customers.filter(customer => {
      const query = searchQuery.toLocaleLowerCase('tr-TR');
      const matchesSearch = (
        customer.name?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.city?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.district?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.representative?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.phone?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.contact_person?.toLocaleLowerCase('tr-TR').includes(query) ||
        customer.address?.toLocaleLowerCase('tr-TR').includes(query)
      );
      
      const matchesRepresentative = selectedRepresentative === 'all' || customer.representative === selectedRepresentative;
      const matchesCity = selectedCity === 'all' || customer.city === selectedCity;
      
      // Cihaz filtresi
      let matchesDevice = true;
      const deviceCount = customer.device_count || 0;
      if (deviceFilter === '0') {
        matchesDevice = deviceCount === 0;
      } else if (deviceFilter === '1-10') {
        matchesDevice = deviceCount >= 1 && deviceCount <= 10;
      } else if (deviceFilter === '11-50') {
        matchesDevice = deviceCount >= 11 && deviceCount <= 50;
      } else if (deviceFilter === '51-100') {
        matchesDevice = deviceCount >= 51 && deviceCount <= 100;
      } else if (deviceFilter === '100+') {
        matchesDevice = deviceCount > 100;
      }
      
      return matchesSearch && matchesRepresentative && matchesCity && matchesDevice;
    });
    
    // Backend'den zaten sıralı geliyor (newest first), sadece sortBy değişirse tekrar sırala
    if (sortBy === 'newest') {
      return filtered; // Backend sıralamasını koru
    }
    
    return filtered.sort((a, b) => {
      switch(sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name, 'tr');
        case 'name-desc':
          return b.name.localeCompare(a.name, 'tr');
        case 'city-asc':
          return (a.city || '').localeCompare(b.city || '', 'tr');
        case 'city-desc':
          return (b.city || '').localeCompare(a.city || '', 'tr');
        case 'arvento-desc':
          return (b.device_count || 0) - (a.device_count || 0);
        case 'arvento-asc':
          return (a.device_count || 0) - (b.device_count || 0);
        case 'representative-asc':
          return (a.representative || '').localeCompare(b.representative || '', 'tr');
        case 'representative-desc':
          return (b.representative || '').localeCompare(a.representative || '', 'tr');
        default:
          return 0;
      }
    });
  }, [customers, searchQuery, sortBy, selectedRepresentative, selectedCity, deviceFilter]);

  // Reset to page 1 when search query or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, selectedRepresentative, selectedCity, deviceFilter]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Customer load error:', error);
      // Handle token errors specially
      try {
        handleTokenError(error);
      } catch (tokenError) {
        toast.error(safeToastError(tokenError));
        return;
      }
      toast.error(safeToastError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Users load error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Zorunlu alan kontrolü
    const requiredFields = [
      { field: 'name', label: 'Müşteri Adı' },
      { field: 'phone', label: 'Telefon' },
      { field: 'email', label: 'E-posta' },
      { field: 'address', label: 'Adres' },
      { field: 'city', label: 'Şehir' },
      { field: 'district', label: 'İlçe' },
      { field: 'contact_person', label: 'İletişim Kişisi' },
      { field: 'tax_number', label: 'Vergi Numarası' },
      { field: 'tax_office', label: 'Vergi Dairesi' }
    ];
    
    const missingFields = requiredFields.filter(f => !formData[f.field]?.trim());
    if (missingFields.length > 0) {
      toast.error(`Lütfen zorunlu alanları doldurun: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }
    
    try {
      const dataToSubmit = {
        ...formData,
        device_count: formData.device_count === '' ? 0 : parseInt(formData.device_count) || 0
      };
      await createCustomer(dataToSubmit);
      toast.success('Müşteri başarıyla eklendi');
      setShowAddDialog(false);
      loadCustomers();
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        district: '',
        contact_person: '',
        representative: '',
        device_count: '0',
        tax_number: '',
        tax_office: ''
      });
    } catch (error) {
      console.error('Customer create error:', error);
      toast.error('Müşteri eklenirken hata oluştu: ' + (error.response?.data?.detail?.[0]?.msg || error.message));
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const result = await bulkImportCustomers(file);
      // Backend'den gelen response: { customers_added, customers_skipped, total_errors, message, errors, success }
      const message = result.message || `${result.customers_added || 0} müşteri başarıyla içe aktarıldı`;
      toast.success(message);
      setShowImportDialog(false);
      loadCustomers();
    } catch (error) {
      console.error('Import error:', error);
      // Safe error handling to prevent React rendering issues
      const errorMessage = safeToastError(error);
      toast.error(errorMessage);
    }
  };

  const handleExport = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const isFiltered = searchQuery.trim() !== '' || 
                       selectedRepresentative !== 'all' || 
                       selectedCity !== 'all' ||
                       deviceFilter !== 'all' ||
                       sortBy !== 'newest';
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredCustomers : customers;
    
    const exportData = dataToExport.map(customer => ({
      'Müşteri Adı': customer.name,
      'Yetkili Kişi': customer.contact_person || '',
      'Telefon': customer.phone || '',
      'E-posta': customer.email || '',
      'Adres': customer.address || '',
      'Şehir': customer.city || '',
      'İlçe': customer.district || '',
      'Toplam Cihaz Adedi': customer.device_count || 0,
      'Pasif Cihaz Adedi': customer.inactive_device_count || 0,
      'Temsilci': customer.representative || '',
      'Arvento Sayısı': customer.arvento_count || 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Müşteriler');
    
    const fileName = isFiltered 
      ? `musteriler_filtreli_${exportData.length}_adet.xlsx`
      : `tum_musteriler_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} müşteri Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        'Müşteri Adı': 'ABC Lojistik A.Ş.',
        'Telefon': '5551234567',
        'E-posta': 'info@abclojistik.com',
        'Şehir': 'İstanbul',
        'İlçe': 'Kadıköy',
        'İletişim Kişisi': 'Ahmet Yılmaz',
        'Toplam Cihaz Adedi': 15,
        'Pasif Cihaz Adedi': 3,
        'Temsilci': 'Rengimdemirtas'
      },
      {
        'Müşteri Adı': 'XYZ Taşımacılık Ltd.',
        'Telefon': '5559876543',
        'E-posta': 'iletisim@xyztasimacilik.com',
        'Şehir': 'Ankara',
        'İlçe': 'Çankaya',
        'İletişim Kişisi': 'Fatma Demir',
        'Toplam Cihaz Adedi': 8,
        'Pasif Cihaz Adedi': 1,
        'Temsilci': 'Rengimdemirtas'
      },
      {
        'Müşteri Adı': 'DEF Kargo Hizmetleri',
        'Telefon': '5554567890',
        'E-posta': 'bilgi@defkargo.com.tr',
        'Şehir': 'İzmir',
        'İlçe': 'Konak',
        'İletişim Kişisi': 'Mehmet Özkan',
        'Toplam Cihaz Adedi': 25,
        'Pasif Cihaz Adedi': 5,
        'Temsilci': 'Rengimdemirtas'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Örnek Müşteriler');
    XLSX.writeFile(wb, 'ornek_musteriler_sablon.xlsx');
    toast.success('Örnek Excel şablonu indirildi');
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Bu müşteriyi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteCustomer(customerId);
      toast.success('Müşteri silindi');
      loadCustomers();
    } catch (error) {
      toast.error('Müşteri silinirken hata oluştu');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('TÜM müşterileri silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!')) return;
    if (!window.confirm('GERÇEKTEN tüm müşterileri silmek istiyor musunuz?')) return;
    
    try {
      await deleteAllCustomers();
      toast.success('Tüm müşteriler silindi');
      loadCustomers();
    } catch (error) {
      toast.error('Müşteriler silinirken hata oluştu');
    }
  };

  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Müşteriler yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader 
        icon={Users}
        title="Müşteriler"
        subtitle={`Toplam ${filteredCustomers.length} müşteri`}
      >
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (open) {
            // Dialog açıldığında formu sıfırla
            setFormData({
              name: '',
              phone: '',
              email: '',
              address: '',
              city: '',
              district: '',
              contact_person: '',
              representative: user?.role === 'temsilci' ? user?.username : '',
              device_count: '0',
              tax_number: '',
              tax_office: ''
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
              <Plus className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Yeni Müşteri</span>
              <span className="sm:hidden">Ekle</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
              <DialogHeader>
                <DialogTitle>Yeni Müşteri Ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Müşteri Adı *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Telefon *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    type="tel"
                    required
                  />
                </div>
                <div>
                  <Label>E-posta *</Label>
                  <Input
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    type="email"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Şehir *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>İlçe *</Label>
                    <Input
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Adres *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Cadde, sokak, bina no..."
                    required
                  />
                </div>
                <div>
                  <Label>İletişim Kişisi *</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Vergi Numarası *</Label>
                    <Input
                      value={formData.tax_number}
                      onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Vergi Dairesi *</Label>
                    <Input
                      value={formData.tax_office}
                      onChange={(e) => setFormData({ ...formData, tax_office: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Toplam Cihaz Adedi *</Label>
                  <Input
                    value={formData.device_count}
                    onChange={(e) => setFormData({ ...formData, device_count: e.target.value })}
                    type="number"
                    min="0"
                    placeholder="Toplam cihaz sayısı"
                    required
                  />
                </div>
                {user?.role === 'admin' && (
                  <div>
                    <Label>Temsilci *</Label>
                    <Select value={formData.representative} onValueChange={(value) => setFormData({ ...formData, representative: value })} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Temsilci seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.username} value={u.username}>{u.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white min-h-11">
                  Müşteri Ekle
                </Button>
              </form>
            </DialogContent>
          </Dialog>

        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogTrigger asChild>
            <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
              <Upload className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">İçe Aktar</span>
              <span className="sm:hidden">Aktar</span>
            </Button>
            </DialogTrigger>
            <DialogContent className="max-w-full sm:max-w-md mx-4">
              <DialogHeader>
                <DialogTitle>Excel'den Müşteri İçe Aktarma</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  <strong>Sadece "Müşteri Adı" zorunludur.</strong> Diğer tüm sütunlar opsiyoneldir.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <p className="font-semibold text-green-900">📥 Örnek Excel Şablonu</p>
                    <Button 
                      onClick={handleDownloadSample}
                      size="sm"
                      variant="outline"
                      className="text-green-700 border-green-300 hover:bg-green-100"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      İndir
                    </Button>
                  </div>
                  <p className="text-sm text-green-800">
                    Örnek veri ile hazır şablonu indirin, düzenleyin ve yükleyin.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                  <p className="font-semibold text-blue-900 mb-2">📋 Desteklenen Sütunlar:</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>• Müşteri Adı (zorunlu)</div>
                    <div>• Telefon</div>
                    <div>• E-posta</div>
                    <div>• Şehir</div>
                    <div>• İlçe</div>
                    <div>• İletişim Kişisi</div>
                    <div>• Toplam Cihaz Adedi</div>
                    <div>• Pasif Cihaz Adedi</div>
                    <div>• Temsilci</div>
                  </div>
                  <p className="text-blue-800 mt-2 text-xs">
                    💡 Boş satırlar otomatik olarak atlanır.
                  </p>
                </div>
                
                <div>
                  <Label>Excel Dosyası Seçin (.xlsx, .xls)</Label>
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="mt-1"
                  />
                </div>
              </div>
            </DialogContent>
          </Dialog>

        {user?.role === 'admin' && (
          <Button onClick={handleExport} className="bg-white text-blue-600 hover:bg-blue-50 shadow-md flex-1 sm:flex-none min-h-11">
            <Download className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
        )}

        {user?.role === 'admin' && (
          <Button
            onClick={handleDeleteAll}
            className="bg-white text-red-600 hover:bg-red-50 shadow-md flex-1 sm:flex-none min-h-11"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span className="hidden sm:inline">Tümünü Sil</span>
          </Button>
        )}
      </PageHeader>

      {/* Search and Filters - Geliştirilmiş */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Arama */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Ad, telefon, şehir, yetkili ile ara..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="pl-11"
              data-testid="search-customers-input"
            />
          </div>
          
          {/* Şehir Filtresi */}
          <div>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger>
                <SelectValue placeholder="Şehir" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Şehirler</SelectItem>
                {uniqueCities.map(city => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Temsilci Filtresi */}
          <div>
            <Select value={selectedRepresentative} onValueChange={setSelectedRepresentative}>
              <SelectTrigger>
                <SelectValue placeholder="Temsilci" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Temsilciler</SelectItem>
                {users.map(u => (
                  <SelectItem key={u.username} value={u.username}>
                    {u.full_name || u.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Cihaz Filtresi */}
          <div>
            <Select value={deviceFilter} onValueChange={setDeviceFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Cihaz Sayısı" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Cihazlar</SelectItem>
                <SelectItem value="0">Cihaz Yok (0)</SelectItem>
                <SelectItem value="1-10">1-10 Cihaz</SelectItem>
                <SelectItem value="11-50">11-50 Cihaz</SelectItem>
                <SelectItem value="51-100">51-100 Cihaz</SelectItem>
                <SelectItem value="100+">100+ Cihaz</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Sıralama - İkinci Satır */}
        <div className="mt-3">
          <Select value={sortBy} onValueChange={(newValue) => {
            const oldValue = sortBy;
            setSortBy(newValue);
            pushAction({
              undo: () => setSortBy(oldValue)
            });
          }}>
            <SelectTrigger>
              <SelectValue placeholder="Sırala" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">En Yeni Kayıtlar</SelectItem>
              <SelectItem value="name-asc">İsim (A-Z)</SelectItem>
              <SelectItem value="name-desc">İsim (Z-A)</SelectItem>
              <SelectItem value="city-asc">Şehir (A-Z)</SelectItem>
              <SelectItem value="city-desc">Şehir (Z-A)</SelectItem>
              <SelectItem value="representative-asc">Temsilci (A-Z)</SelectItem>
              <SelectItem value="representative-desc">Temsilci (Z-A)</SelectItem>
              <SelectItem value="arvento-desc">Cihaz Sayısı (Çok-Az)</SelectItem>
              <SelectItem value="arvento-asc">Cihaz Sayısı (Az-Çok)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {paginatedCustomers.map((customer) => (
          <div 
            key={customer.id} 
            className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#193046] cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(`/customers/${customer.id}`)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-1">{customer.name}</h3>
                {customer.contact_person && (
                  <div className="text-sm text-gray-600 mb-1">Yetkili: {customer.contact_person}</div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                  <User className="w-4 h-4" />
                  <span>{customer.representative || 'Atanmamış'}</span>
                </div>
              </div>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-bold">
                {customer.arvento_count || 0}
              </div>
            </div>
            
            <div className="space-y-2 mb-3">
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${customer.phone}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{customer.phone}</a>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${customer.email}`} className="hover:text-[#193046]" onClick={(e) => e.stopPropagation()}>{customer.email}</a>
                </div>
              )}
              {customer.city && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>{customer.city}{customer.district ? ` / ${customer.district}` : ''}</span>
                </div>
              )}
            </div>

            {user?.role === 'admin' && (
              <div className="flex justify-end">
                <Button
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteCustomer(customer.id);
                  }}
                  className="min-h-11 px-4"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap w-[25%]">Müşteri Adı</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap w-[18%]">Yetkili</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap w-[15%]">Telefon</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap w-[12%]">Şehir</th>
                <th className="px-4 py-3 text-left text-sm whitespace-nowrap w-[15%]">Temsilci</th>
                <th className="px-4 py-3 text-center text-sm whitespace-nowrap w-[8%]">Cihaz</th>
                <th className="px-4 py-3 text-center text-sm whitespace-nowrap w-[7%]">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedCustomers.map((customer) => (
                <tr 
                  key={customer.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-sm max-w-[200px]">
                    <div className="truncate" title={customer.name}>{customer.name}</div>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-[150px]">
                    <div className="truncate" title={customer.contact_person}>{customer.contact_person || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">{customer.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm">{customer.city || '-'}</td>
                  <td className="px-4 py-3 text-sm max-w-[120px]">
                    <div className="truncate" title={customer.representative}>{customer.representative || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                      {customer.device_count || 0}
                    </span>
                  </td>
                  {/* Silme butonu - admin, abonelik, temsilci için */}
                  {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                    <td className="px-4 py-3 text-center text-sm">
                      <div className="flex justify-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCustomer(customer.id);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                  {!['admin', 'abonelik', 'temsilci'].includes(user?.role) && <td></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredCustomers.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2 bg-white p-2 rounded-lg shadow">
          <div className="text-xs text-gray-700">
            Gösterilen: {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredCustomers.length)} / {filteredCustomers.length}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              variant="outline"
              size="sm"
              className="h-8 text-xs px-2"
            >
              Önceki
            </Button>
            <span className="px-2 py-1 text-xs font-medium">
              {currentPage} / {Math.ceil(filteredCustomers.length / itemsPerPage)}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredCustomers.length / itemsPerPage)}
              variant="outline"
              size="sm"
              className="h-8 text-xs px-2"
            >
              Sonraki
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;